import React, { useState } from 'react';
import {
  Grid,
  Typography,
  Box,
  Paper,
  TextField,
  Button,
  Divider,
  Chip,
  FormControlLabel,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Add as AddIcon,
  Image as ImageIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  Language as WebsiteIcon,
} from '@mui/icons-material';

interface BusinessHours {
  day: string;
  open: string;
  close: string;
  closed: boolean;
}

const BusinessProfile: React.FC = () => {
  const [editing, setEditing] = useState(false);
  const [openHoursDialog, setOpenHoursDialog] = useState(false);

  const businessHours: BusinessHours[] = [
    { day: 'Monday', open: '9:00 AM', close: '5:00 PM', closed: false },
    { day: 'Tuesday', open: '9:00 AM', close: '5:00 PM', closed: false },
    { day: 'Wednesday', open: '9:00 AM', close: '5:00 PM', closed: false },
    { day: 'Thursday', open: '9:00 AM', close: '5:00 PM', closed: false },
    { day: 'Friday', open: '9:00 AM', close: '5:00 PM', closed: false },
    { day: 'Saturday', open: '10:00 AM', close: '3:00 PM', closed: false },
    { day: 'Sunday', open: '10:00 AM', close: '3:00 PM', closed: true },
  ];

  const platforms = [
    { name: 'Google My Business', connected: true },
    { name: 'Facebook', connected: true },
    { name: 'Yelp', connected: true },
    { name: 'TripAdvisor', connected: false },
  ];

  const handleSave = () => {
    setEditing(false);
    // TODO: Save changes to backend
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Business Profile</Typography>
        <Button
          variant="contained"
          startIcon={editing ? <SaveIcon /> : <EditIcon />}
          onClick={() => editing ? handleSave() : setEditing(true)}
        >
          {editing ? 'Save Changes' : 'Edit Profile'}
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Basic Information */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Basic Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Business Name"
                  defaultValue="Sample Business Name"
                  disabled={!editing}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Business Description"
                  defaultValue="A sample business description that highlights our services and unique value proposition."
                  disabled={!editing}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  defaultValue="(555) 123-4567"
                  disabled={!editing}
                  InputProps={{
                    startAdornment: <PhoneIcon sx={{ mr: 1 }} />,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Website"
                  defaultValue="www.samplebusiness.com"
                  disabled={!editing}
                  InputProps={{
                    startAdornment: <WebsiteIcon sx={{ mr: 1 }} />,
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  defaultValue="123 Business St, City, State 12345"
                  disabled={!editing}
                  InputProps={{
                    startAdornment: <LocationIcon sx={{ mr: 1 }} />,
                  }}
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Business Hours */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Business Hours</Typography>
              {editing && (
                <Button
                  startIcon={<EditIcon />}
                  onClick={() => setOpenHoursDialog(true)}
                >
                  Edit Hours
                </Button>
              )}
            </Box>
            <Grid container spacing={2}>
              {businessHours.map((hours) => (
                <Grid item xs={12} key={hours.day}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body1" sx={{ width: '100px' }}>
                      {hours.day}
                    </Typography>
                    {hours.closed ? (
                      <Typography color="text.secondary">Closed</Typography>
                    ) : (
                      <Typography>
                        {hours.open} - {hours.close}
                      </Typography>
                    )}
                  </Box>
                  {hours.day !== 'Sunday' && <Divider sx={{ mt: 1 }} />}
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} md={4}>
          {/* Connected Platforms */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Connected Platforms
            </Typography>
            <Grid container spacing={1}>
              {platforms.map((platform) => (
                <Grid item xs={12} key={platform.name}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" role="article">
                    <Typography variant="body1">{platform.name}</Typography>
                    <Chip
                      label={platform.connected ? 'Connected' : 'Not Connected'}
                      color={platform.connected ? 'success' : 'default'}
                      size="small"
                    />
                  </Box>
                </Grid>
              ))}
            </Grid>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<AddIcon />}
              sx={{ mt: 2 }}
            >
              Connect New Platform
            </Button>
          </Paper>

          {/* Photos */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Business Photos
            </Typography>
            <Grid container spacing={1}>
              {[1, 2, 3, 4].map((photo) => (
                <Grid item xs={6} key={photo}>
                  <Paper
                    sx={{
                      aspectRatio: '1',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'grey.100',
                    }}
                  >
                    <ImageIcon data-testid="ImageIcon" sx={{ fontSize: 40, color: 'grey.400' }} />
                  </Paper>
                </Grid>
              ))}
            </Grid>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<AddIcon />}
              sx={{ mt: 2 }}
            >
              Add Photos
            </Button>
          </Paper>
        </Grid>
      </Grid>

      {/* Business Hours Dialog */}
      <Dialog
        open={openHoursDialog}
        onClose={() => setOpenHoursDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Business Hours</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {businessHours.map((hours, index) => (
              <Grid item xs={12} key={hours.day}>
                <Box display="flex" alignItems="center" gap={2}>
                  <Typography sx={{ width: '100px' }}>{hours.day}</Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!hours.closed}
                        onChange={() => {}}
                      />
                    }
                    label="Open"
                  />
                  {!hours.closed && (
                    <>
                      <TextField
                        label="Open"
                        defaultValue={hours.open}
                        size="small"
                      />
                      <TextField
                        label="Close"
                        defaultValue={hours.close}
                        size="small"
                      />
                    </>
                  )}
                </Box>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenHoursDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => setOpenHoursDialog(false)}
          >
            Save Hours
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BusinessProfile;
